import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, BookOpen, UserPen, ClipboardList, Heart, Clock, CheckCircle2, XCircle, ChevronDown, ClipboardPen, FileUp, Globe } from 'lucide-react';
import { useAuthContext, useModal } from '@/contexts';
import { authService } from '../../services/authService';
import { logger } from '@/utils/logger';
import { useTutorCompletionStatus } from '@/hooks/api/useTutorCompletionStatus';
import { useParentCases } from '@/hooks/api/useCases';
import { isFullyVerified as getIsFullyVerified } from '@tutor/types';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { stripLocaleFromPathname, useOptionalI18n } from '@/i18n';
import { Link, useLocalizedPath } from '@/i18n/navigation';
import NavBanner from './NavBanner';
import { getActiveNavBanners } from './navBannerRegistry';

const NavBar: React.FC = () => {
  const { t } = useOptionalI18n();
  const { pathname } = useLocation();
  const localizePath = useLocalizedPath();
  const currentPath = stripLocaleFromPathname(pathname);
  const isActive = (to: string) => to === '/' ? currentPath === '/' : currentPath.startsWith(to);
  const desktopLink = (to: string) =>
    `transition-colors font-medium ${isActive(to) ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'}`;
  const mobileLink = (to: string) =>
    `block font-medium py-2 transition-colors relative ${isActive(to) ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'}`;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const [showSupportMenu, setShowSupportMenu] = useState(false);
  const [showMobileSupportMenu, setShowMobileSupportMenu] = useState(false);
  const [dismissedBannerIds, setDismissedBannerIds] = useState<string[]>([]);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [mobileNavOffset, setMobileNavOffset] = useState(64);
  const [isMobileNavVisible, setIsMobileNavVisible] = useState(true);
  const { firebaseUser, backendUser, loading, profile } = useAuthContext();
  const { openLoginModal, openTutorRegistration, openParentRegistration } = useModal();
  const { data: completionStatus, isLoading: isCompletionLoading, isFetching: isCompletionFetching } = useTutorCompletionStatus(profile?.id, profile?.role === 'tutor');
  const shouldCheckParentCaseActions = profile?.role === 'parent';
  const { data: parentActionNeededData } = useParentCases(
    { status: 'action_needed', page: 1, limit: 1 },
    shouldCheckParentCaseActions
  );
  const userMenuRef = useRef<HTMLDivElement>(null);
  const supportMenuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const lastScrollYRef = useRef(0);
  const lastTouchYRef = useRef<number | null>(null);
  const scrollDirectionRef = useRef<'up' | 'down' | null>(null);
  const hasIdentityDoc = profile?.verificationDocuments?.some(d => d.type === 'identity');
  const hasQualificationDoc = profile?.verificationDocuments?.some(d => d.type === 'qualification');
  const hasCertificateDoc = profile?.verificationDocuments?.some(d => d.type === 'certificate');
  const hasSupportDoc = profile?.verificationDocuments?.some(d => d.type === 'support');
  const hasReviewSubmissionDoc = hasQualificationDoc || hasCertificateDoc || hasSupportDoc;
  const hasPrimaryCertificateDoc = hasQualificationDoc || hasCertificateDoc;
  const isFullyVerified = getIsFullyVerified(profile);
  const requiredMissingFieldKeys = (completionStatus?.missingFields ?? [])
    .filter((field) => field.severity === 'required')
    .map((field) => field.key)
    .join(',');
  const requiredMissingFieldCount = requiredMissingFieldKeys
    ? requiredMissingFieldKeys.split(',').length
    : 0;
  const shouldShowCompleteProfileLink = !profile || (profile?.role === 'tutor' && !isFullyVerified);
  const shouldShowCompleteProfileDot =
    profile?.role === 'tutor' &&
    !isFullyVerified &&
    requiredMissingFieldCount > 0;
  const shouldShowUploadDocumentsLink =
    profile?.role === 'tutor' && !isFullyVerified;
  const shouldShowUploadDocumentsDot =
    shouldShowUploadDocumentsLink && !(hasPrimaryCertificateDoc && hasIdentityDoc);
  const shouldShowRejectedProfileDot =
    profile?.role === 'tutor' && isFullyVerified && profile?.verificationStatus === 'rejected';
  const shouldShowParentCaseActionDot =
    profile?.role === 'parent' && (parentActionNeededData?.pagination.total ?? 0) > 0;
  const pendingProfileActionCount = [
    Boolean(firebaseUser && !profile),
    shouldShowCompleteProfileDot,
    shouldShowUploadDocumentsDot,
    shouldShowRejectedProfileDot,
    shouldShowParentCaseActionDot,
  ].filter(Boolean).length;
  const shouldShowProfileMenuBadge = pendingProfileActionCount > 0;
  const isProfileLoading = Boolean(firebaseUser && (loading || !profile));
  const displayName = profile?.fullName || t('nav.userFallback');
  const displayEmail = profile?.email || backendUser?.email || '';
  const avatarUrl = profile?.profilePhotoUrl || profile?.profilePicture;
  const avatarInitial = displayName.trim().charAt(0).toUpperCase() || 'U';
  const activeRole = profile?.role || backendUser?.currentRole || null;
  const canViewAllClasses = activeRole === 'tutor';
  const shouldShowBecomeTutorLink = activeRole !== 'parent';
  const showFindTutorCta = !firebaseUser || activeRole === 'parent';
  const showRegisterTutorCta = !firebaseUser;
  const desktopNavLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/tutors', label: t('nav.allTutors') },
    ...(canViewAllClasses ? [{ to: '/cases', label: t('nav.allCases') }] : []),
    ...(shouldShowBecomeTutorLink ? [{ to: '/teach', label: t('nav.becomeTutor') }] : []),
    { to: '/about-us', label: t('nav.aboutUs') },
  ];
  const supportLinks = [
    { to: '/how-it-works', label: t('nav.howItWorks') },
    { to: '/help-center', label: t('nav.helpCenter') },
    { to: '/tuition-fee', label: t('nav.tuitionFeeReference') },
  ];
  const activeBanners = getActiveNavBanners(t).filter((banner) => !dismissedBannerIds.includes(banner.id));
  const mobileNavLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/tutors', label: t('nav.allTutors') },
    ...(canViewAllClasses ? [{ to: '/cases', label: t('nav.allCases') }] : []),
    ...(shouldShowBecomeTutorLink ? [{ to: '/teach', label: t('nav.becomeTutor') }] : []),
    { to: '/about-us', label: t('nav.aboutUs') },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const AvatarSkeleton = ({ sizeClassName }: { sizeClassName: string }) => (
    <div className={`${sizeClassName} rounded-full bg-gray-200 animate-pulse`} />
  );

  const UserTextSkeleton = () => (
    <div className="space-y-1.5">
      <div className="h-3.5 w-24 rounded bg-gray-200 animate-pulse" />
      <div className="h-3 w-32 rounded bg-gray-100 animate-pulse" />
    </div>
  );

  const ProfileMenuBadge = () => (
    <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold leading-none text-white">
      {pendingProfileActionCount}
    </span>
  );

  const UserAvatar = ({ sizeClassName, iconClassName }: { sizeClassName: string; iconClassName: string }) => (
    avatarUrl && !avatarLoadError ? (
      <img
        src={avatarUrl}
        alt={displayName}
        className={`${sizeClassName} rounded-full object-cover`}
        onError={() => setAvatarLoadError(true)}
      />
    ) : (
      <div className={`${sizeClassName} rounded-full bg-primary text-white flex items-center justify-center`}>
        {avatarInitial ? (
          <span className="text-sm font-semibold">{avatarInitial}</span>
        ) : (
          <User className={iconClassName} />
        )}
      </div>
    )
  );

  useEffect(() => {
    setAvatarLoadError(false);
  }, [avatarUrl]);

  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const updateMobileNavOffset = () => {
      setMobileNavOffset(navElement.getBoundingClientRect().height || 64);
    };

    updateMobileNavOffset();

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => {
          updateMobileNavOffset();
        });

    resizeObserver?.observe(navElement);
    window.addEventListener('resize', updateMobileNavOffset);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMobileNavOffset);
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      setIsMobileNavVisible(true);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMobileNavVisible(true);
    setIsBannerVisible(true);
  }, [currentPath]);

  useEffect(() => {
    const mobileBreakpoint = 768;
    const showThreshold = 6;
    const hideThreshold = 12;

    const setScrollDirection = (nextDirection: 'up' | 'down' | null) => {
      if (nextDirection) {
        scrollDirectionRef.current = nextDirection;
      }
    };

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < 2) return;
      setScrollDirection(event.deltaY > 0 ? 'down' : 'up');
    };

    const handleTouchStart = (event: TouchEvent) => {
      lastTouchYRef.current = event.touches[0]?.clientY ?? null;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const currentTouchY = event.touches[0]?.clientY;

      if (currentTouchY === undefined || lastTouchYRef.current === null) {
        return;
      }

      const deltaY = currentTouchY - lastTouchYRef.current;

      if (Math.abs(deltaY) >= 2) {
        setScrollDirection(deltaY < 0 ? 'down' : 'up');
      }

      lastTouchYRef.current = currentTouchY;
    };

    const handleTouchEnd = () => {
      lastTouchYRef.current = null;
    };

    const handleScroll = () => {
      const currentY = window.scrollY;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      const inferredDirection =
        delta > 0 ? 'down' : delta < 0 ? 'up' : null;
      const scrollDirection = scrollDirectionRef.current ?? inferredDirection;

      if (window.innerWidth >= mobileBreakpoint) {
        if (currentY <= 8) {
          setIsBannerVisible(true);
        } else if (scrollDirection === 'down' && delta > hideThreshold) {
          setIsBannerVisible(false);
        } else if (scrollDirection === 'up' && delta < -showThreshold) {
          setIsBannerVisible(true);
        }

        setIsMobileNavVisible(true);
        lastScrollYRef.current = currentY;
        return;
      }

      if (isMenuOpen) {
        setIsMobileNavVisible(true);
        lastScrollYRef.current = currentY;
        return;
      }

      if (currentY <= 16) {
        setIsMobileNavVisible(true);
      } else if (delta > hideThreshold && currentY > mobileNavOffset) {
        setIsMobileNavVisible(false);
      } else if (delta < -showThreshold) {
        setIsMobileNavVisible(true);
      }

      lastScrollYRef.current = currentY;
    };

    lastScrollYRef.current = window.scrollY;
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMenuOpen, mobileNavOffset]);

  // Lock body scroll when mobile menu or user dropdown is open
  useEffect(() => {
    if (isMenuOpen || showUserMenu) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isMenuOpen, showUserMenu]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (supportMenuRef.current && !supportMenuRef.current.contains(event.target as Node)) {
        setShowSupportMenu(false);
      }
    };

    // Handle escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setShowSupportMenu(false);
      }
    };

    if (showUserMenu || showSupportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showUserMenu, showSupportMenu]);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      setShowUserMenu(false);
    } catch (error) {
      logger.error('NavBar', 'Logout error', error);
    }
  };

  // Debug log for Complete Profile button logic
  useEffect(() => {
    if (profile?.role === 'tutor') {
      const isMissingDocs = !hasReviewSubmissionDoc || !hasIdentityDoc;
      const completeProfileDotReasons = {
        profileIncompleteFromApi: completionStatus?.isProfileComplete === false,
        requiredMissingFieldsFromApi: requiredMissingFieldKeys ? requiredMissingFieldKeys.split(',') : [],
        blockedBecauseNotTutor: profile?.role !== 'tutor',
        blockedBecauseFullyVerified: isFullyVerified,
      };

      // isProfileComplete from API = form fields are complete (does NOT require docs)
      // completionPercentage from API = may include doc requirements, so < 100 even when form is done
      // ROOT CAUSE of bug: NavBar JSX was using completionPercentage !== 100 instead of !isProfileComplete
      logger.group('NavBar', 'Complete Profile indicator', () => {
        logger.log('NavBar', 'Reason summary', {
          shouldShowCompleteProfileDot,
          completeProfileDotReasons,
          note: 'NavBar uses saved profile/context + completion API data, not unsaved modal formData.',
        });

        logger.log('NavBar', 'State snapshot', {
          // Query states to verify caching/loading issues
          isCompletionLoading,
          isCompletionFetching,
          hasFirebaseUser: !!firebaseUser,
          hasProfile: !!profile,
          profileId: profile?.id,
          // Raw API values
          api_isProfileComplete: completionStatus?.isProfileComplete,
          api_completionPercentage: completionStatus?.completionPercentage,
          api_verificationStatus: completionStatus?.verificationStatus,
          api_hasAboutMe: completionStatus?.hasAboutMe,
          api_missingFields: completionStatus?.missingFields ?? [],
          // Profile context values
          profile_verificationStatus: profile?.verificationStatus,
          profile_aboutMeLength: profile?.aboutMe?.trim().length ?? 0,
          profile_examResultsCount: profile?.examResults?.length ?? 0,
          profile_tutorExperienceTypes: profile?.tutorExperienceTypes ?? [],
          hasIdentityDoc,
          hasQualificationDoc,
          hasCertificateDoc,
          hasSupportDoc,
          hasReviewSubmissionDoc,
          isFullyVerified,
          isMissingDocs,
          // Old (buggy) condition: used completionPercentage === 100 as proxy for form completion
          OLD_showModal: completionStatus !== undefined && completionStatus.completionPercentage !== 100,
          OLD_showDocLink: completionStatus?.completionPercentage === 100 && isMissingDocs,
          // Current conditions: profile prompt = incomplete fields; docs link = available until verified
          showProfilePrompt: shouldShowCompleteProfileDot,
          requiredMissingFieldCount,
          hasPrimaryCertificateDoc,
          showDocLink: shouldShowUploadDocumentsLink,
          showDocDot: shouldShowUploadDocumentsDot,
        });
      });
    }
  }, [profile, completionStatus, isCompletionLoading, isCompletionFetching, firebaseUser, shouldShowCompleteProfileDot, shouldShowUploadDocumentsLink, shouldShowUploadDocumentsDot, hasIdentityDoc, hasQualificationDoc, hasCertificateDoc, hasSupportDoc, hasReviewSubmissionDoc, isFullyVerified, hasPrimaryCertificateDoc, requiredMissingFieldKeys, requiredMissingFieldCount]);

  return (
    <nav
      ref={navRef}
      className={`bg-white shadow-md sticky top-0 z-header transform-gpu transition-transform duration-300 ease-out ${
        isMobileNavVisible ? 'translate-y-0' : '-translate-y-full'
      } md:translate-y-0`}
    >
      <div
        aria-hidden={!isBannerVisible}
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          isBannerVisible ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {activeBanners.map((banner) => (
          <NavBanner
            key={banner.id}
            banner={banner}
            onDismiss={() => {
              setDismissedBannerIds((current) =>
                current.includes(banner.id) ? current : [...current, banner.id]
              );
            }}
          />
        ))}
      </div>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <button
            type="button"
            className="flex items-center"
            onClick={() => {
              window.location.href = localizePath('/');
            }}
          >
            <span className="text-2xl font-bold text-primary font-brand">Tutor Founder</span>
          </button>

          {/* Desktop Navigation - Center */}
          <div className="hidden md:flex items-center justify-center flex-1 space-x-8">
            {desktopNavLinks.map(({ to, label }) => (
              <Link key={to} to={to} className={`${desktopLink(to)} relative group`}>
                {label}
                {isActive(to) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            ))}
            <div className="relative" ref={supportMenuRef}>
              <button
                type="button"
                onClick={() => setShowSupportMenu((prev) => !prev)}
                className={`relative flex items-center gap-1.5 transition-colors font-medium ${
                  isActive('/how-it-works') || isActive('/help-center') || isActive('/tuition-fee')
                    ? 'text-primary font-semibold'
                    : 'text-gray-700 hover:text-primary'
                }`}
              >
                <span>{t('nav.support')}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showSupportMenu ? 'rotate-180' : ''}`} />
                {(isActive('/how-it-works') || isActive('/help-center') || isActive('/tuition-fee')) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-primary" />
                )}
              </button>
              {showSupportMenu && (
                <div className="absolute left-1/2 top-full z-40 mt-3 w-56 -translate-x-1/2 rounded-2xl border border-gray-200 bg-white p-2 shadow-lg">
                  {supportLinks.map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setShowSupportMenu(false)}
                      className="block rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary"
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Desktop user dropdown backdrop */}
          {showUserMenu && (
            <div
              className="hidden md:block fixed inset-0 z-30"
              onClick={() => setShowUserMenu(false)}
            />
          )}

          {/* Right Side - Primary CTA & Login/User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {showFindTutorCta && (
              <button
                onClick={openParentRegistration}
                className="inline-flex items-center rounded-2xl bg-[#3F6B57] px-5 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#365D4C]"
              >
                {t('nav.findTutor')}
              </button>
            )}
            {showRegisterTutorCta && (
              <button
                onClick={() => openTutorRegistration()}
                className="inline-flex items-center rounded-2xl border border-black bg-white px-5 py-2 text-sm font-semibold text-gray-900 transition-colors duration-200 hover:bg-gray-50"
              >
                {t('nav.registerTutor')}
              </button>
            )}
            {firebaseUser ? (
              <div className="relative" ref={userMenuRef}>
                {isProfileLoading ? (
                  <div className="flex items-center gap-2 px-3 py-2">
                    <AvatarSkeleton sizeClassName="h-8 w-8" />
                    <div className="h-3.5 w-20 rounded bg-gray-200 animate-pulse" />
                  </div>
                ) : (
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="relative flex items-center gap-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors max-w-[160px]"
                  >
                    <UserAvatar sizeClassName="h-8 w-8 flex-shrink-0" iconClassName="h-4 w-4" />
                    <span className="font-medium text-gray-700 truncate">
                      {displayName}
                    </span>
                    {shouldShowProfileMenuBadge && (
                      <ProfileMenuBadge />
                    )}
                  </button>
                )}

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border border-gray-200 z-40">
                    <div className="px-4 py-2 border-b border-gray-200">
                      {isProfileLoading ? (
                        <UserTextSkeleton />
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900">{displayName}</p>
                          <p className="text-xs text-gray-500 truncate">{displayEmail}</p>
                        </>
                      )}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {profile?.role && (
                          <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-primary text-white capitalize">
                            {profile.role}
                          </span>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'pending' && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded-full w-fit">
                            <Clock className="h-3 w-3 text-amber-600" />
                            <span className="text-xs font-semibold text-amber-700">{t('nav.pendingVerification')}</span>
                          </div>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'approved' && isFullyVerified && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full w-fit">
                            <CheckCircle2 className="h-3 w-3 text-green-600" />
                            <span className="text-xs font-semibold text-green-700">{t('nav.verified')}</span>
                          </div>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'approved' && !isFullyVerified && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-full w-fit">
                            <CheckCircle2 className="h-3 w-3 text-blue-600" />
                            <span className="text-xs font-semibold text-blue-700">{t('nav.approved')}</span>
                          </div>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'rejected' && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-red-50 rounded-full w-fit">
                            <XCircle className="h-3 w-3 text-red-600" />
                            <span className="text-xs font-semibold text-red-700">{t('nav.rejected')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {profile?.role === 'parent' && (
                      <Link
                        to="/parent/my-cases"
                        role="menuitem"
                        onClick={() => setShowUserMenu(false)}
                        className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <BookOpen className="h-4 w-4" />
                        {t('nav.myCases')}
                        {shouldShowParentCaseActionDot && (
                          <span className="absolute top-2 right-3 h-2.5 w-2.5 rounded-full bg-red-500" />
                        )}
                      </Link>
                    )}
                    {profile?.role === 'parent' && (
                      <Link
                        to="/parent/my-liked-tutors"
                        role="menuitem"
                        onClick={() => setShowUserMenu(false)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Heart className="h-4 w-4" />
                        {t('nav.myLikedTutors')}
                      </Link>
                    )}
                    {profile?.role === 'parent' && (
                      <Link
                        to="/parent/edit-profile"
                        role="menuitem"
                        onClick={() => setShowUserMenu(false)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <UserPen className="h-4 w-4" />
                        {t('nav.editProfile')}
                      </Link>
                    )}
                    {shouldShowCompleteProfileLink && !profile && (
                      <button
                        role="menuitem"
                        onClick={() => {
                          setShowUserMenu(false);
                          openTutorRegistration({ entryPoint: 'complete-profile' });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <ClipboardPen className="h-4 w-4" />
                        {t('nav.completeProfile')}
                        <span className="absolute top-2 right-2 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                      </button>
                    )}
                    {profile?.role === 'tutor' && !isFullyVerified && (
                        <button
                          role="menuitem"
                          onClick={() => {
                            setShowUserMenu(false);
                            openTutorRegistration({ entryPoint: 'complete-profile' });
                          }}
                          className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <ClipboardPen className="h-4 w-4" />
                          {t('nav.completeProfile')}
                          {shouldShowCompleteProfileDot && (
                            <span className="absolute top-2 right-2 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                          )}
                        </button>
                    )}
                    {shouldShowUploadDocumentsLink && (
                        <Link
                          to="/upload-documentation"
                          role="menuitem"
                          onClick={() => setShowUserMenu(false)}
                          className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <FileUp className="h-4 w-4" />
                          {t('nav.uploadDocuments')}
                          {shouldShowUploadDocumentsDot && (
                            <span className="absolute top-2 right-2 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                          )}
                        </Link>
                    )}
                    {profile?.role === 'tutor' && isFullyVerified && (
                      <Link
                        to="/tutor/edit-profile"
                        role="menuitem"
                        onClick={() => setShowUserMenu(false)}
                        className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <UserPen className="h-4 w-4" />
                        {t('nav.editProfile')}
                        {shouldShowRejectedProfileDot && (
                          <span className="absolute top-1 right-3 h-2.5 w-2.5 bg-red-500 rounded-full"></span>
                        )}
                      </Link>
                    )}
                    {profile?.role === 'tutor' && (
                      <Link
                        to="/tutor/my-applications"
                        role="menuitem"
                        onClick={() => setShowUserMenu(false)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <ClipboardList className="h-4 w-4" />
                        {t('nav.myApplications')}
                      </Link>
                    )}
                    <button
                      role="menuitem"
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" />
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => openLoginModal({ initialMode: 'login', ignorePending: true })}
                className="text-gray-700 hover:text-primary transition-colors font-medium"
              >
                {t('nav.login')}
              </button>
            )}
            <LanguageSwitcher
              compact
              icon={<Globe className="h-4 w-4" />}
              showLabel={false}
              showChevron={false}
              className="w-11"
              menuWidthMode="content"
              dropdownButtonClassName="justify-center border-transparent bg-transparent px-0 shadow-none focus:ring-0 hover:bg-transparent"
              dropdownLabelClassName="sr-only"
              dropdownClassName="[&_button]:rounded-full [&_button]:py-1.5 [&_button]:text-sm"
            />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="relative md:hidden p-2 text-gray-700 hover:text-primary transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            {!isMenuOpen && shouldShowProfileMenuBadge && (
              <ProfileMenuBadge />
            )}
          </button>
        </div>

        {/* Mobile Navigation Backdrop */}
        {isMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-40"
            style={{ top: mobileNavOffset }}
            onClick={toggleMenu}
          />
        )}

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            className="md:hidden fixed inset-x-0 z-50 overflow-y-auto bg-white shadow-xl py-4 space-y-3 border-t px-4"
            style={{ top: mobileNavOffset, maxHeight: `calc(100vh - ${mobileNavOffset}px)` }}
          >
            {mobileNavLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={mobileLink(to)}
                onClick={toggleMenu}
              >
                {isActive(to) && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-full bg-primary" />
                )}
                <span className={isActive(to) ? 'pl-3' : ''}>{label}</span>
              </Link>
            ))}

            <div className="border-b border-gray-100 pb-1">
              <button
                type="button"
                onClick={() => setShowMobileSupportMenu((prev) => !prev)}
                className={`flex w-full items-center justify-between py-2 text-left font-medium transition-colors ${
                  isActive('/how-it-works') || isActive('/help-center') || isActive('/tuition-fee')
                    ? 'text-primary font-semibold'
                    : 'text-gray-700 hover:text-primary'
                }`}
              >
                <span>{t('nav.support')}</span>
                <ChevronDown className={`h-5 w-5 transition-transform ${showMobileSupportMenu ? 'rotate-180' : ''}`} />
              </button>
              {showMobileSupportMenu && (
                <div className="mt-1 space-y-1 pb-2 pl-3">
                  {supportLinks.map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className={mobileLink(to)}
                      onClick={toggleMenu}
                    >
                      {isActive(to) && (
                        <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-primary" />
                      )}
                      <span className={isActive(to) ? 'pl-3' : ''}>{label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <LanguageSwitcher
              icon={<Globe className="h-4 w-4" />}
              showLabel={false}
              className="rounded-2xl border border-gray-100 bg-gray-50 p-3"
              dropdownClassName="[&_button]:border-gray-200 [&_button]:bg-white [&_button]:pl-9 [&_button]:text-sm [&_button]:shadow-none"
              onLocaleChange={toggleMenu}
            />

            {(showFindTutorCta || showRegisterTutorCta) && (
              <div className="flex flex-col gap-3 pt-2">
                {showFindTutorCta && (
                  <button
                    onClick={() => {
                      toggleMenu();
                      openParentRegistration();
                    }}
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-[#3F6B57] px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#365D4C]"
                  >
                    {t('nav.findTutor')}
                  </button>
                )}
                {showRegisterTutorCta && (
                  <button
                    onClick={() => {
                      toggleMenu();
                      openTutorRegistration();
                    }}
                    className="inline-flex w-full items-center justify-center rounded-2xl border border-black bg-white px-5 py-3 text-sm font-semibold text-gray-900 transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('nav.registerTutor')}
                  </button>
                )}
              </div>
            )}

            {firebaseUser ? (
              <div className="pt-3 border-t space-y-2">
                {isProfileLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <AvatarSkeleton sizeClassName="h-8 w-8" />
                    <UserTextSkeleton />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowMobileUserMenu(prev => !prev)}
                    aria-expanded={showMobileUserMenu}
                    className="relative w-full flex items-center gap-2 py-2 text-left hover:bg-gray-50 rounded transition-colors"
                  >
                    <UserAvatar sizeClassName="h-8 w-8 flex-shrink-0" iconClassName="h-5 w-5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                      <p className="text-xs text-gray-500 truncate">{displayEmail}</p>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        {profile?.role && (
                          <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-primary text-white capitalize">
                            {profile.role}
                          </span>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'pending' && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded-full w-fit">
                            <Clock className="h-3 w-3 text-amber-600" />
                            <span className="text-xs font-semibold text-amber-700">{t('nav.pendingVerification')}</span>
                          </div>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'approved' && isFullyVerified && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full w-fit">
                            <CheckCircle2 className="h-3 w-3 text-green-600" />
                            <span className="text-xs font-semibold text-green-700">{t('nav.verified')}</span>
                          </div>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'approved' && !isFullyVerified && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-full w-fit">
                            <CheckCircle2 className="h-3 w-3 text-blue-600" />
                            <span className="text-xs font-semibold text-blue-700">{t('nav.approved')}</span>
                          </div>
                        )}
                        {profile?.role === 'tutor' && profile?.verificationStatus === 'rejected' && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-red-50 rounded-full w-fit">
                            <XCircle className="h-3 w-3 text-red-600" />
                            <span className="text-xs font-semibold text-red-700">{t('nav.rejected')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-500 transition-transform ${showMobileUserMenu ? 'rotate-180' : ''}`}
                    />
                    {shouldShowProfileMenuBadge && (
                      <ProfileMenuBadge />
                    )}
                  </button>
                )}
                {showMobileUserMenu && (
                <>
                {profile?.role === 'parent' && (
                  <Link
                    to="/parent/my-cases"
                    onClick={toggleMenu}
                    className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <BookOpen className="h-4 w-4" />
                    {t('nav.myCases')}
                    {shouldShowParentCaseActionDot && (
                      <span className="absolute top-2 right-3 h-2.5 w-2.5 rounded-full bg-red-500" />
                    )}
                  </Link>
                )}
                {profile?.role === 'parent' && (
                  <Link
                    to="/parent/edit-profile"
                    onClick={toggleMenu}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <UserPen className="h-4 w-4" />
                    {t('nav.editProfile')}
                  </Link>
                )}
                {shouldShowCompleteProfileLink && !profile && (
                  <button
                    onClick={() => {
                      toggleMenu();
                      openTutorRegistration({ entryPoint: 'complete-profile' });
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <ClipboardPen className="h-4 w-4" />
                    {t('nav.completeProfile')}
                    <span className="absolute top-2 right-2 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                  </button>
                )}
                  {profile?.role === 'tutor' && !isFullyVerified && (
                      <button
                        onClick={() => {
                          toggleMenu();
                          openTutorRegistration({ entryPoint: 'complete-profile' });
                        }}
                        className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                      >
                        <ClipboardPen className="h-4 w-4" />
                        {t('nav.completeProfile')}
                        {shouldShowCompleteProfileDot && (
                          <span className="absolute top-2 right-2 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                        )}
                      </button>
                  )}
                  {shouldShowUploadDocumentsLink && (
                      <Link
                        to="/upload-documentation"
                        onClick={toggleMenu}
                        className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                      >
                        <FileUp className="h-4 w-4" />
                        {t('nav.uploadDocuments')}
                        {shouldShowUploadDocumentsDot && (
                          <span className="absolute top-2 right-2 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                        )}
                      </Link>
                  )}
                {profile?.role === 'tutor' && isFullyVerified && (
                  <Link
                    to="/tutor/edit-profile"
                    onClick={toggleMenu}
                    className="relative w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <UserPen className="h-4 w-4" />
                    {t('nav.editProfile')}
                    {shouldShowRejectedProfileDot && (
                      <span className="absolute top-1 right-3 h-2.5 w-2.5 bg-red-500 rounded-full"></span>
                    )}
                  </Link>
                )}
                {profile?.role === 'tutor' && (
                  <Link
                    to="/tutor/my-applications"
                    onClick={toggleMenu}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <ClipboardList className="h-4 w-4" />
                    {t('nav.myApplications')}
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    toggleMenu();
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  {t('nav.logout')}
                </button>
                </>
                )}
              </div>
            ) : (
              <button
                onClick={() => {
                  openLoginModal({ initialMode: 'login', ignorePending: true });
                  toggleMenu();
                }}
                className="text-gray-700 hover:text-primary transition-colors font-medium py-2"
              >
                {t('nav.login')}
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;
